'use client';
import { createContext, useContext, useState, useRef, useEffect, ReactNode, useCallback } from 'react';
import { useSnackbar } from '@/app/contexts/snack-bar-provider';
import utilsService from '@/app/helpers/utilsService';
import { isAppBridge } from '@/app/helpers/appStorageMethod';
import { actionFeedServices } from '@/app/service/actionFeedService';
import { PostTypeConfig } from '@/app/constants/typedef';
import { useUser } from '@/app/hooks/useUser';
import { FeedManager, FetchMethod } from '../pattern/ClassManagerMethodPost';
import { PostModel } from '../models/posts/Posts';
import { useParams } from 'next/navigation';
import { useGlobalPostState, PostUpdateType, PostUpdate } from './global-post-state-context';

interface PostContextProps {
    posts: any[];
    isLoading: boolean;
    hasNext: boolean;
    totalPosts: number;
    hasError: boolean;
    setFilter: (filter: string) => void;
    setType: (type: PostTypeConfig) => void;
    loadMore: () => Promise<void>;
    hiddenPost: (id: string) => void;
    deletePost: (id: string) => void;
    updatePost: (updatedPost: PostModel) => void;
    addPost: (newPost: PostModel) => void;
    updatePostField: (postId: string, field: keyof PostModel, newValue: any) => void;
    onRetryWhenHasError: () => void;
    refresh: () => Promise<void>;
    updateUserFollowStatus: (userId: string, isFollowing: boolean) => void;
}

const PostContext = createContext<PostContextProps | undefined>(undefined);

export const usePostContext = () => {
    const context = useContext(PostContext);
    if (!context) {
        throw new Error('usePostContext must be used within a PostProvider');
    }
    return context;
};

export const PostProvider = ({ children }: { children: ReactNode }) => {
    const [posts, setPosts] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [cursor, setCursor] = useState<string | null>(null);
    const { id: partnerId } = useParams();
    const [filter, setFilter] = useState<string>('');
    const [type, setType] = useState<PostTypeConfig | null>(null);
    const totalPosts = useRef<number>(0);
    const isFetching = useRef<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);
    const feedManager = useRef(new FeedManager(10)).current;
    const fetchController = useRef<AbortController | null>(null);
    const { addSnackbar, setMarginBottom } = useSnackbar();
    const globalPostState = useGlobalPostState(); // Get global post state
    const lastRequestIdRef = useRef<number>(0);
    const { user, auth } = useUser();

    useEffect(() => {
        setMarginBottom(80);
    }, []);

    // Subscribe to global post state updates
    useEffect(() => {
        const unsubscribe = globalPostState.subscribe((update: PostUpdate) => {
            // Ignore updates from this context to prevent loops
            if (update.source === 'PostProvider') return;
            switch (update.type) {
                case PostUpdateType.FOLLOW_STATUS_CHANGED: {
                    const { userId, isFollowing } = update.payload as any;
                    // When user is blocked (isFollowing = false), remove all their posts
                    if (!isFollowing) {
                        removePostsByUserId(userId);
                    } else {
                        updateUserFollowStatusLocal(userId, isFollowing);
                    }
                    break;
                }
                case PostUpdateType.POST_DELETED: {
                    const { postId } = update.payload as any;
                    hiddenPostLocal(postId);
                    break;
                }
                case PostUpdateType.POST_UPDATED: {
                    const { post } = update.payload as any;
                    updatePostLocal(post);
                    break;
                }
                case PostUpdateType.POST_HIDDEN: {
                    const { postId } = update.payload as any;
                    hiddenPostLocal(postId);
                    break;
                }
                case PostUpdateType.POST_ADDED: {
                    const { post } = update.payload as any;
                    addPostLocal(post);
                    break;
                }
                case PostUpdateType.POST_FIELD_UPDATED: {
                    const { postId, field, value } = update.payload as any;
                    updatePostFieldLocal(postId, field, value);
                    break;
                }
                default:
                    break;
            }
        });

        return unsubscribe;
    }, [globalPostState]);

    const fetchPosts = async (filter: string, reset?: boolean) => {
        if (hasError && !reset) return;
        if (reset) setHasError(false);

        const requestId = ++lastRequestIdRef.current;
        if (fetchController.current) {
            fetchController.current.abort();
        }
        fetchController.current = new AbortController();
        const signal = fetchController.current.signal;
        isFetching.current = true;
        try {
            setIsLoading(true);
            const method = getFetchMethod(type);
            const { data, total, cursor: cur } = await feedManager.fetchDataForFeed<any>(method, { type, filter, partnerId }, reset ? null : cursor, signal);
            
            // Only update if this is the newest request
            if (requestId === lastRequestIdRef.current) {
                totalPosts.current = total;
                setCursor(cur ?? null);
                setPosts((prevPosts) => (reset ? data : [...prevPosts, ...data]));
                setIsLoading(false);
                isFetching.current = false;
                setHasError(false);
            }
        } catch (error: any) {
            const isLastRequest = requestId === lastRequestIdRef.current;
            if (isLastRequest) {
                const getMessage = utilsService.getErrorMessage(error);
                setIsLoading(false);
                isFetching.current = false;

                if (error.name !== 'AbortError') {
                    // Suppress snackbar for 401 errors on App Bridge as they will be handled by auto-refresh
                    const status = error.response?.status || error.data?.status || error.status;
                    const isTokenExpiredMessage = utilsService.getErrorMessage(error)?.toLowerCase().includes('token has expired');
                    
                    if (isAppBridge() && (status === 401 || isTokenExpiredMessage)) {
                        setHasError(true);
                        return;
                    }
                    
                    setHasError(true);
                    addSnackbar({
                        message: getMessage || 'Something went wrong',
                        severity: 'error',
                        autoHideDuration: 3000,
                        vertical: 'bottom',
                        horizontal: 'center',
                        type: 'toast',
                    });
                }
            }
        }
    };

    const getFetchMethod = (type: string | null): FetchMethod => {
        switch (type) {
            case PostTypeConfig.Challenges:
                return FetchMethod.challenges;
            case PostTypeConfig.Posts:
                return FetchMethod.posts;
            case PostTypeConfig.Products:
                return FetchMethod.products;
            case PostTypeConfig.News:
                return FetchMethod.news;
            case PostTypeConfig.fetchForMyChannel:
                return FetchMethod.fetchForMyChannel;
            case PostTypeConfig.fetchForPartner:
                return FetchMethod.fetchForPartner;
            default:
                throw new Error('Unknown filter');
        }
    };

    // ==================== Local State Update Helpers ====================
    // These update local state without dispatching to global (to prevent loops)

    const hiddenPostLocal = (id: string) => {
        setPosts((prevPosts) => prevPosts.filter((post) => post.id !== id));
    };

    const removePostsByUserId = (userId: string) => {
        setPosts((prevPosts) =>
            prevPosts.filter((post) => String(post.owner?.id) !== String(userId))
        );
    };

    const updatePostLocal = (updatedPost: any) => {
        setPosts((prevPosts) => prevPosts.map((post) => (post.id === updatedPost.id ? updatedPost : post)));
    };

    const addPostLocal = (newPost: any) => {
        const currentType = type;
        if (currentType !== null && [PostTypeConfig.fetchForMyChannel, PostTypeConfig.fetchForPartner, PostTypeConfig.Posts].includes(currentType)) {
            validateAddPostLocal(newPost);
        }
    };

    const validateAddPostLocal = (newPost: any) => {
        if (type && ['fetchForMyChannel'].includes(type) && ['comments', 'likes', 'bookmarks'].includes(filter)) return; //do nothing
        setPosts((prevPosts) => [newPost, ...prevPosts]);
    };

    const updatePostFieldLocal = (postId: string, field: keyof PostModel, newValue: any) => {
        setPosts((prevPosts) =>
            prevPosts.map((post) => {
                if (post.id === postId) {
                    const updatedPost = { ...post };
                    updatedPost[field] = newValue;
                    return Object.assign(Object.create(Object.getPrototypeOf(post)), updatedPost);
                }
                return post;
            })
        );
    };

    const updateUserFollowStatusLocal = (userId: string, isFollowing: boolean) => {
        setPosts((prevPosts) =>
            prevPosts.map((post) => {
                // Convert both to string for comparison to handle number/string mismatch
                const postOwnerId = String(post.owner?.id);
                const targetUserId = String(userId);
                if (postOwnerId === targetUserId) {
                    return {
                        ...post,
                        owner: {
                            ...post.owner,
                            isFollowing,
                        },
                    };
                }
                return post;
            })
        );
    };

    // ==================== Public Methods (dispatch to global) ====================

    const hiddenPost = async (id: string) => {
        try {
            // Call API to hide post
            await actionFeedServices.hidePost({ type: 'posts', id });
            // Update local state
            hiddenPostLocal(id);
            // Dispatch to global
            globalPostState.hidePost(id, 'PostProvider');
        } catch (error) {
            const errorMessage = utilsService.getErrorMessage(error);
            addSnackbar({
                message: errorMessage || 'Some thing went wrong',
                severity: 'error',
                autoHideDuration: 3000,
                vertical: 'bottom',
                horizontal: 'center',
                type: 'toast',
            });
        }
    };

    const deletePost = async (id: string) => {
        try {
            await actionFeedServices.deleteFeedForOnlyPosts({ type: 'posts', id });
            // Update local state
            hiddenPostLocal(id);
            // Dispatch to global
            globalPostState.deletePost(id, 'PostProvider');
            addSnackbar({
                message: '게시물을 삭제했어요.',
                severity: 'info',
                autoHideDuration: 3000,
                vertical: 'bottom',
                horizontal: 'center',
                type: 'toast',
            });
        } catch (error: any) {
            const errorMessage = utilsService.getErrorMessage(error);
            addSnackbar({
                message: errorMessage || 'Some thing went wrong',
                severity: 'error',
                autoHideDuration: 3000,
                vertical: 'bottom',
                horizontal: 'center',
                type: 'toast',
            });
        }
    };

    const updatePost = (updatedPost: any) => {
        // Update local state
        updatePostLocal(updatedPost);
        // Dispatch to global
        globalPostState.updatePost(updatedPost, 'PostProvider');
    };

    const addPost = (newPost: any) => {
        const currentType = type;
        if (currentType !== null && [PostTypeConfig.fetchForMyChannel, PostTypeConfig.fetchForPartner, PostTypeConfig.Posts].includes(currentType)) {
            validateAddPost(newPost);
        }
    };

    const validateAddPost = (newPost: any) => {
        if (type && ['fetchForMyChannel'].includes(type) && ['comments', 'likes', 'bookmarks'].includes(filter)) return; //do nothing
        // Update local state
        addPostLocal(newPost);
        // Dispatch to global
        globalPostState.addPost(newPost, 'PostProvider');
    };

    const loadMore = useCallback(async () => {
        if (cursor && !isLoading && !isFetching.current && !hasError) {
            await fetchPosts(filter);
        }
    }, [posts, cursor, isLoading, hasError]);

    const onRetryWhenHasError = useCallback(() => {
        setCursor(null);
        setPosts([]);
    }, [filter, type]);

    const updatePostField = (postId: string, field: keyof PostModel, newValue: any) => {
        // Update local state
        updatePostFieldLocal(postId, field, newValue);
        // Dispatch to global
        globalPostState.updatePostField(postId, field, newValue, 'PostProvider');
    };

    const updateUserFollowStatus = (userId: string, isFollowing: boolean) => {
        // Update local state
        updateUserFollowStatusLocal(userId, isFollowing);
        // Dispatch to global
        globalPostState.updateUserFollowStatus(userId, isFollowing, 'PostProvider');
    };

    useEffect(() => {
        if (filter && type) {
            setCursor(null);
            setPosts([]);
            fetchPosts(filter, true);
        }
    }, [filter, type, user?.id, auth?.accessToken]);

    const refresh = useCallback(async () => {
        if (filter && type) {
            await fetchPosts(filter, true);
        }
    }, [filter, type]);

    const hasNext = posts.length < totalPosts.current && !hasError;
    const value = {
        posts,
        isLoading,
        hasNext,
        hasError,
        setFilter,
        totalPosts: totalPosts.current,
        setType,
        loadMore,
        deletePost,
        hiddenPost,
        updatePost,
        addPost,
        updatePostField,
        onRetryWhenHasError,
        refresh,
        updateUserFollowStatus,
    };

    return <PostContext.Provider value={value}>{children}</PostContext.Provider>;
};
